import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Lock, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const AGE_BANDS = [{ id: "8-10", label: "8–10" }, { id: "11-13", label: "11–13" }, { id: "14-16", label: "14–16" }, { id: "17-19", label: "17–19" }];
const STYLES = ["talkative", "private", "sensitive", "strong-willed", "easygoing", "not sure"];

export default function Children() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ age_band: "", nickname: "", style_tag: "" });
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/children").then(({ data }) => setChildren(data.children)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.age_band) { toast.error("Please pick an age band"); return; }
    setSaving(true);
    try { await api.post("/children", form); toast.success("Child profile added"); setShowForm(false); setForm({ age_band: "", nickname: "", style_tag: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.delete(`/children/${id}`); toast.success("Profile removed"); load(); } catch { toast.error("Couldn't remove"); } };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="children-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">My family</h1>
          <p style={{ color: "var(--text-muted)" }}>Up to 5 profiles. Only an age band, optional nickname and a style — encrypted &amp; private.</p>
        </div>
        {children.length < 5 && <button className="btn btn-primary whitespace-nowrap" onClick={() => setShowForm(true)} data-testid="add-child-btn"><Plus size={16} /> Add child</button>}
      </div>

      <div className="rounded-xl p-4 flex gap-3 text-sm" style={{ background: "var(--sage-tint)" }}>
        <Lock size={18} style={{ color: "var(--sage)" }} className="shrink-0 mt-0.5" /> No real names or photos needed. Everything here is encrypted with a key only your account can use.
      </div>

      {loading ? <Loader2 className="animate-spin" style={{ color: "var(--sage)" }} /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {children.map((c) => (
            <div key={c.id} className="dk-card p-6 flex items-start justify-between" data-testid="child-card">
              <div>
                <div className="font-serif text-[22px]">{c.nickname || "Your child"}</div>
                <div className="badge-soft mt-2">{c.age_band}</div>
                {c.style_tag && <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{c.style_tag}</div>}
              </div>
              <button className="btn btn-ghost px-2" onClick={() => remove(c.id)} data-testid={`delete-child-${c.id}`} aria-label="Remove"><Trash2 size={16} style={{ color: "var(--error)" }} /></button>
            </div>
          ))}
          {children.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No profiles yet. Add your first child to personalise your decodes.</p>}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(47,58,52,.35)" }} onClick={() => setShowForm(false)}>
          <div className="dk-card p-6 w-full max-w-md space-y-4 anim-in" onClick={(e) => e.stopPropagation()} data-testid="child-form">
            <div className="flex items-center justify-between"><h2 className="font-serif text-[22px] font-medium">Add a child</h2><button className="btn btn-ghost px-2" onClick={() => setShowForm(false)}><X size={18} /></button></div>
            <div>
              <label className="text-sm font-medium">Age band</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {AGE_BANDS.map((b) => (
                  <button key={b.id} onClick={() => setForm({ ...form, age_band: b.id })} className={`btn ${form.age_band === b.id ? "btn-secondary" : "btn-outline"}`} data-testid={`form-age-${b.id}`}>{b.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Nickname (optional)</label>
              <input className="dk-input mt-1" maxLength={40} value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="form-nickname" />
            </div>
            <div>
              <label className="text-sm font-medium">Style (optional)</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {STYLES.map((s) => <button key={s} onClick={() => setForm({ ...form, style_tag: form.style_tag === s ? "" : s })} className={`badge-soft ${form.style_tag === s ? "" : "opacity-70"}`} style={form.style_tag === s ? { background: "var(--sage)", color: "#fff" } : {}}>{s}</button>)}
              </div>
            </div>
            <button className="btn btn-primary w-full" disabled={saving} onClick={add} data-testid="form-save-btn">{saving && <Loader2 size={16} className="animate-spin" />} Add profile</button>
          </div>
        </div>
      )}
    </div>
  );
}
